import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { Indicator, Popover, SegmentedControl, SimpleGrid } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconFilter, IconLayoutGrid, IconTable } from '@tabler/icons-react';
import { Table } from '../../core/atoms/Table';
import { Pagination } from '../../core/atoms/Pagination';
import { Stack } from '../../core/atoms/Stack';
import { Group } from '../../core/atoms/Group';
import { Box } from '../../core/atoms/Box';
import { Text } from '../../core/atoms/Text';
import { Loader } from '../../core/atoms/Loader';
import { Select } from '../../core/atoms/Select';
import { Icon } from '../../core/atoms/Icon';
import { ActionIcon } from '../../core/atoms/ActionIcon';
import { SearchBar } from '../../core/molecules/SearchBar';
import { EmptyState } from '../../core/molecules/EmptyState';
import { BottomDrawer } from '../../core/molecules/BottomDrawer';
import type { CoreProps, PaginatedResult, ResourceQuery } from '../../core/types';
import { useShouldRender } from '../../hooks/useShouldRender';
import { usePlatform } from '../../hooks/usePlatform';

export interface ResourceLayoutColumn<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  width?: string | number;
}

export interface ResourceLayoutSocket {
  on: (event: string, handler: (...args: unknown[]) => void) => void;
  off: (event: string, handler: (...args: unknown[]) => void) => void;
}

export interface ResourceLayoutSocketEvent<T> {
  event: string;
  transform: (payload: unknown, currentRows: T[]) => T[];
}

export interface ResourceLayoutProps<T> extends CoreProps {
  data: PaginatedResult<T> | undefined;
  isLoading: boolean;
  isFetching?: boolean;
  onQueryChange: (q: ResourceQuery) => void;
  defaultPageLimit?: number;
  getRowId: (row: T) => string;
  columns: ResourceLayoutColumn<T>[];
  onRowClick?: (row: T) => void;
  renderCard: (row: T) => ReactNode;
  /**
   * Compact list row renderer (flush divider rows). Falls back to `renderCard`
   * when omitted. Not used for grid / desktop table.
   */
  renderListItem?: (row: T) => ReactNode;
  /** Fixed mobile/tablet presentation. No layout toggle on compact screens. */
  mobileView?: 'list' | 'grid';
  socketRef?: RefObject<ResourceLayoutSocket | null>;
  socketEvents?: ResourceLayoutSocketEvent<T>[];
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  /** Rendered beside the layout switch (desktop) / filter icon (compact). */
  addButton?: ReactNode;
  /** Filter UI shown in Popover (desktop) or BottomDrawer (compact). */
  filterContent?: ReactNode;
  /** Badge count on the filter icon when filters are active. */
  filterActiveCount?: number;
  /**
   * When true, the filter icon/popover is only rendered on compact (mobile/tablet) screens.
   * Use this when the page already shows its own filter controls (e.g. Tabs) on desktop.
   */
  filterOnlyCompact?: boolean;
  /**
   * Changing this value resets page to 1 and clears accumulated infinite-scroll rows
   * (e.g. when an external filter/tab changes).
   */
  resetKey?: string | number;
}

const PAGE_SIZE_OPTIONS = [
  { value: '10', label: '10 / page' },
  { value: '20', label: '20 / page' },
  { value: '50', label: '50 / page' },
  { value: '100', label: '100 / page' },
];

export function ResourceLayout<T>({
  data,
  isLoading,
  isFetching,
  onQueryChange,
  defaultPageLimit = 20,
  getRowId,
  columns,
  onRowClick,
  renderCard,
  renderListItem,
  mobileView = 'list',
  socketRef,
  socketEvents,
  searchable = true,
  searchPlaceholder = 'Search...',
  emptyTitle = 'No data',
  emptyDescription,
  addButton,
  filterContent,
  filterActiveCount = 0,
  filterOnlyCompact = false,
  resetKey,
  testId,
  view = 'all',
}: ResourceLayoutProps<T>) {
  const shouldRender = useShouldRender(view);
  const platform = usePlatform();
  const isMobilePlatform = platform === 'mobile';
  const isTablet = useMediaQuery('(max-width: 1024px)');
  const isCompact = isMobilePlatform || Boolean(isTablet);

  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(defaultPageLimit);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [webViewMode, setWebViewMode] = useState<'table' | 'grid'>('table');
  const [localRows, setLocalRows] = useState<T[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  /** Compact infinite-scroll: stop once the last page is known. */
  const [reachedEnd, setReachedEnd] = useState(false);
  /** True after requesting the next page until that response is applied. */
  const [loadingMore, setLoadingMore] = useState(false);

  const onQueryChangeRef = useRef(onQueryChange);
  onQueryChangeRef.current = onQueryChange;
  const getRowIdRef = useRef(getRowId);
  getRowIdRef.current = getRowId;
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const isCompactRef = useRef(isCompact);
  isCompactRef.current = isCompact;
  /** Highest page number successfully applied into localRows. */
  const appliedPageRef = useRef(0);
  const prevDataRef = useRef(data);
  const resetKeyRef = useRef(resetKey);
  const pendingResetRef = useRef(false);

  function resetInfiniteScroll() {
    setReachedEnd(false);
    setLoadingMore(false);
    appliedPageRef.current = 0;
  }

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
      resetInfiniteScroll();
    }, 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  // External filter/tab change → restart from page 1
  useEffect(() => {
    if (resetKey === undefined) return;
    if (resetKeyRef.current !== resetKey) {
      resetKeyRef.current = resetKey;
      pendingResetRef.current = true;
      prevDataRef.current = undefined;
    }
    setPage(1);
    setLocalRows([]);
    resetInfiniteScroll();
  }, [resetKey]);

  useEffect(() => {
    onQueryChangeRef.current({
      page,
      pageLimit,
      search: debouncedSearch,
    });
  }, [page, pageLimit, debouncedSearch]);

  // Apply server pages: replace on desktop / page 1; append on compact page 2+.
  useEffect(() => {
    if (!data) return;
    // After resetKey changes, ignore cached payloads until the new fetch settles.
    if (pendingResetRef.current) {
      if (isFetching) return;
      pendingResetRef.current = false;
    }
    // Wait until the in-flight load-more request finishes
    if (loadingMore && isFetching) return;
    // Don't apply the previous page's cached payload as if it were the next page
    if (loadingMore && prevDataRef.current === data) return;

    const rows = data.rows ?? [];
    const dataTotal = data.total ?? 0;
    const dataLimit = data.pageLimit ?? pageLimit;
    const dataPage = data.page ?? (loadingMore ? page : 1);
    prevDataRef.current = data;

    if (!isCompactRef.current) {
      setLocalRows((prev) => {
        if (
          prev.length === rows.length &&
          prev.every(
            (row, i) => getRowIdRef.current(row) === getRowIdRef.current(rows[i]!),
          )
        ) {
          return prev;
        }
        return rows;
      });
      appliedPageRef.current = dataPage;
      setLoadingMore(false);
      return;
    }

    // Stale payload from an earlier page
    if (data.page != null && data.page < page && loadingMore) return;
    if (dataPage < appliedPageRef.current) return;

    if (dataPage <= 1) {
      // Don't wipe accumulated rows with a stale first page during load-more
      if (loadingMore && page > 1) return;
      setLocalRows(rows);
      appliedPageRef.current = 1;
      setLoadingMore(false);
      setReachedEnd(dataTotal === 0 || rows.length >= dataTotal || rows.length < dataLimit);
      return;
    }

    if (dataPage <= appliedPageRef.current) {
      setLoadingMore(false);
      return;
    }

    setLocalRows((prev) => {
      const seen = new Set(prev.map((row) => getRowIdRef.current(row)));
      const next = [...prev];
      let added = 0;
      for (const row of rows) {
        const id = getRowIdRef.current(row);
        if (!seen.has(id)) {
          seen.add(id);
          next.push(row);
          added += 1;
        }
      }
      const shortPage = rows.length === 0 || rows.length < dataLimit;
      const full = dataTotal > 0 && next.length >= dataTotal;
      const lastPage =
        dataLimit > 0 && dataTotal > 0
          ? dataPage >= Math.ceil(dataTotal / dataLimit)
          : shortPage;
      if (added === 0 || shortPage || full || lastPage) {
        queueMicrotask(() => setReachedEnd(true));
      }
      return next;
    });

    appliedPageRef.current = dataPage;
    setLoadingMore(false);
  }, [data, page, pageLimit, loadingMore, isFetching]);

  // Safety: if a load-more request finishes without unlocking, stop the loop
  useEffect(() => {
    if (!loadingMore || isFetching || isLoading) return;
    const t = window.setTimeout(() => {
      setLoadingMore((still) => {
        if (!still) return false;
        setReachedEnd(true);
        return false;
      });
    }, 100);
    return () => window.clearTimeout(t);
  }, [loadingMore, isFetching, isLoading, data]);

  // Leaving compact → drop back to a single page of results
  useEffect(() => {
    if (!isCompact) {
      setLocalRows(data?.rows ?? []);
      setPage(1);
      resetInfiniteScroll();
    }
  }, [isCompact]); // eslint-disable-line react-hooks/exhaustive-deps -- only on breakpoint flip

  useEffect(() => {
    const socket = socketRef?.current;
    if (!socket || !socketEvents?.length) return;

    const handlers = socketEvents.map((evt) => {
      const handler = (...args: unknown[]) => {
        const payload = args[0];
        setLocalRows((prev) => evt.transform(payload, prev));
      };
      socket.on(evt.event, handler);
      return { event: evt.event, handler };
    });

    return () => {
      for (const { event, handler } of handlers) {
        socket.off(event, handler);
      }
    };
  }, [socketRef, socketEvents]);

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageLimit) || 1);
  const hasMore = !reachedEnd && localRows.length < total && page < totalPages;
  const canAutoLoadMore =
    shouldRender &&
    isCompact &&
    hasMore &&
    localRows.length > 0 &&
    !loadingMore &&
    !isLoading &&
    !isFetching &&
    Boolean(data);

  // One load per intersection; loadingMore blocks until the page is applied.
  // Depend on localRows.length so we re-bind after the sentinel mounts (first page apply).
  useEffect(() => {
    if (!canAutoLoadMore) return;
    const el = loadMoreRef.current;
    if (!el) return;

    let scrollRoot: Element | null = el.parentElement;
    while (scrollRoot) {
      const { overflow, overflowY } = window.getComputedStyle(scrollRoot);
      if (
        overflow === 'auto' ||
        overflow === 'scroll' ||
        overflowY === 'auto' ||
        overflowY === 'scroll'
      ) {
        break;
      }
      scrollRoot = scrollRoot.parentElement;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        setLoadingMore(true);
        setPage((p) => p + 1);
      },
      {
        root: scrollRoot,
        rootMargin: '160px',
        threshold: 0,
      },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [canAutoLoadMore, localRows.length]);

  if (!shouldRender) return null;

  const from = total === 0 ? 0 : (page - 1) * pageLimit + 1;
  const to = Math.min(page * pageLimit, total);

  const TOOLBAR_H = 36;

  const filterButton = filterContent ? (
    <Indicator
      disabled={filterActiveCount <= 0}
      label={filterActiveCount > 0 ? String(filterActiveCount) : undefined}
      size={16}
      color="blue"
      offset={4}
    >
      <ActionIcon
        variant="default"
        size={TOOLBAR_H}
        aria-label="Filters"
        onClick={() => {
          if (isCompact) setFilterOpen(true);
        }}
      >
        <Icon icon={IconFilter} size={16} />
      </ActionIcon>
    </Indicator>
  ) : null;

  const layoutToggle = !isCompact ? (
    <SegmentedControl
      size="xs"
      value={webViewMode}
      onChange={(value) => setWebViewMode(value as 'table' | 'grid')}
      withItemsBorders={false}
      data={[
        {
          value: 'table',
          label: <Icon icon={IconTable} size={14} />,
        },
        {
          value: 'grid',
          label: <Icon icon={IconLayoutGrid} size={14} />,
        },
      ]}
      classNames={{ root: 'rl-layout-toggle', label: 'rl-layout-toggle-label', control: 'rl-layout-toggle-control' }}
      styles={{
        root: {
          height: TOOLBAR_H,
          backgroundColor: 'var(--mantine-color-gray-1)',
          borderRadius: 'var(--mantine-radius-md)',
          padding: 0,
          border: 'none',
        },
        indicator: {
          backgroundColor: 'var(--mantine-primary-color-filled)',
          borderRadius: 'var(--mantine-radius-sm)',
          boxShadow: 'none',
        },
        control: {
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        },
        label: {
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1,
          height: '100%',
          padding: '0 12px',
          color: 'var(--mantine-color-dimmed)',
        },
      }}
    />
  ) : null;

  const toolbar = (
    <Group justify="space-between" align="center" wrap="wrap" gap="sm">
      <Group gap="sm" style={{ flex: isCompact ? 1 : undefined, minWidth: isCompact ? '100%' : undefined }}>
        {searchable ? (
          <Box style={{ width: isCompact ? '100%' : 280, maxWidth: '100%' }}>
            <SearchBar value={search} onChange={setSearch} placeholder={searchPlaceholder} />
          </Box>
        ) : null}
        {isFetching && !isLoading ? <Loader size="sm" /> : null}
      </Group>

      <Group gap="sm" wrap="nowrap">
        {/* Desktop filter popover — hidden when filterOnlyCompact is set */}
        {filterContent && !isCompact && !filterOnlyCompact ? (
          <Popover
            opened={filterOpen}
            onChange={setFilterOpen}
            position="bottom-end"
            withArrow
            shadow="md"
            width={280}
          >
            <Popover.Target>
              <Indicator
                disabled={filterActiveCount <= 0}
                label={filterActiveCount > 0 ? String(filterActiveCount) : undefined}
                size={16}
                color="blue"
                offset={4}
              >
                <ActionIcon
                  variant="default"
                  size={TOOLBAR_H}
                  aria-label="Filters"
                  onClick={() => setFilterOpen((o) => !o)}
                >
                  <Icon icon={IconFilter} size={16} />
                </ActionIcon>
              </Indicator>
            </Popover.Target>
            <Popover.Dropdown>
              <Stack gap="sm">{filterContent}</Stack>
            </Popover.Dropdown>
          </Popover>
        ) : (
          /* On compact (or when filterOnlyCompact is set), show the plain icon that opens the bottom-drawer */
          isCompact ? filterButton : null
        )}
        {addButton}
        {layoutToggle}
      </Group>
    </Group>
  );

  const paginationBar =
    !isCompact && total > 0 ? (
      <Group justify="flex-end" align="center" wrap="wrap" gap="sm">
        <Text size="sm" c="dimmed">
          Showing {from} to {to} of {total}
        </Text>
        <Select
          size="xs"
          w={120}
          data={PAGE_SIZE_OPTIONS}
          value={String(pageLimit)}
          onChange={(value) => {
            if (!value) return;
            setPageLimit(Number(value));
            setPage(1);
          }}
          allowDeselect={false}
        />
        {totalPages > 1 ? (
          <Pagination value={page} onChange={setPage} total={totalPages} size="sm" />
        ) : null}
      </Group>
    ) : null;

  const compactLoadMoreFooter =
    isCompact && localRows.length > 0 ? (
      <Stack align="center" py="sm" gap="xs">
        {loadingMore || (isFetching && page > 1) ? <Loader size="sm" /> : null}
        {hasMore ? (
          <div
            ref={loadMoreRef}
            style={{ height: 24, width: '100%', flexShrink: 0 }}
            aria-hidden
          />
        ) : total > pageLimit ? (
          <Text size="xs" c="dimmed">
            No more data
          </Text>
        ) : null}
      </Stack>
    ) : null;

  function renderTable() {
    return (
      <Table.ScrollContainer minWidth={600}>
        <Table
          withTableBorder
          withColumnBorders
          withRowBorders
          highlightOnHover
          striped
        >
          <Table.Thead>
            <Table.Tr>
              {columns.map((col) => (
                <Table.Th key={col.key} w={col.width}>
                  {col.header}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {localRows.map((row) => (
              <Table.Tr
                key={getRowId(row)}
                style={onRowClick ? { cursor: 'pointer' } : undefined}
                onClick={() => onRowClick?.(row)}
              >
                {columns.map((col) => (
                  <Table.Td key={col.key}>{col.render(row)}</Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    );
  }

  function renderCards(asGrid: boolean) {
    if (asGrid) {
      const cols = isCompact
        ? isMobilePlatform
          ? { base: 2 }
          : { base: 2, sm: 3 }
        : { base: 2, sm: 3, lg: 4 };

      return (
        <SimpleGrid cols={cols} spacing="md">
          {localRows.map((row) => (
            <Box
              key={getRowId(row)}
              style={onRowClick ? { cursor: 'pointer' } : undefined}
              onClick={() => onRowClick?.(row)}
            >
              {renderCard(row)}
            </Box>
          ))}
        </SimpleGrid>
      );
    }

    // Flush divider rows (procure-mobile style). Click is handled inside
    // renderListItem when provided; otherwise wrap for onRowClick.
    const useListItem = Boolean(renderListItem);
    return (
      <Stack gap={0}>
        {localRows.map((row) => {
          const content = useListItem ? renderListItem!(row) : renderCard(row);
          if (useListItem) {
            return <Box key={getRowId(row)}>{content}</Box>;
          }
          return (
            <Box
              key={getRowId(row)}
              style={onRowClick ? { cursor: 'pointer' } : undefined}
              onClick={() => onRowClick?.(row)}
            >
              {content}
            </Box>
          );
        })}
      </Stack>
    );
  }

  let body: ReactNode;
  if (isLoading && !data) {
    body = (
      <Stack align="center" py="xl">
        <Loader />
      </Stack>
    );
  } else if (!localRows.length) {
    body = <EmptyState title={emptyTitle} description={emptyDescription} />;
  } else if (isCompact) {
    // Mobile/tablet: always list or grid from prop — no table, no toggle
    body = renderCards(mobileView === 'grid');
  } else if (webViewMode === 'grid') {
    body = renderCards(true);
  } else {
    body = renderTable();
  }

  return (
    <Stack gap="md" data-testid={testId}>
      {toolbar}
      {body}
      {compactLoadMoreFooter}
      {paginationBar}

      {filterContent && isCompact ? (
        <BottomDrawer
          opened={filterOpen}
          onClose={() => setFilterOpen(false)}
          title="Filters"
        >
          <Stack gap="sm">{filterContent}</Stack>
        </BottomDrawer>
      ) : null}

      {!isCompact ? (
        <style>{`
          .rl-layout-toggle {
            height: 36px !important;
            min-height: 36px;
          }
          .rl-layout-toggle .rl-layout-toggle-control {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100%;
          }
          .rl-layout-toggle .rl-layout-toggle-label {
            display: inline-flex !important;
            align-items: center;
            justify-content: center;
            line-height: 1;
            height: 100%;
          }
          .rl-layout-toggle .rl-layout-toggle-label svg {
            display: block;
          }
          .rl-layout-toggle .rl-layout-toggle-label[data-active] {
            color: white !important;
          }
        `}</style>
      ) : null}
    </Stack>
  );
}
